// Copyright (c) 2021 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { catchError, filter, map, mergeMap } from "rxjs/operators"
import { Action } from "redux"
import { DeleteFavouriteActivityTypeRequest, } from "generated/legebill/site/activitytype/v4/activity_type_pb"
import * as Actions from "../actions"
import { grpc } from "app/rpc/executor"
import { activityTypeServiceClient } from "app/rpc/client"
import * as google_protobuf_empty_pb from "google-protobuf/google/protobuf/empty_pb"
import { isGrpcError } from "../../../lib/rpcutils"
import { StatusCode } from "grpc-web"
import { SiteId } from "../../user-context/slice"
import { t } from "@lingui/macro"
import { showSnackBar } from "../../snackbar/actions"

type DeleteFavouriteActivityTypeRpc = (request: DeleteFavouriteActivityTypeRequest) => Observable<google_protobuf_empty_pb.Empty>

export const deleteFavouriteActivityType = (deleteFavouriteActivityTypeRpc: DeleteFavouriteActivityTypeRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.deleteFavouriteActivityType.match),
      mergeMap(({ payload: { request, siteId, }}) =>
        of(request)
          .pipe(
            mergeMap(deleteFavouriteActivityTypeRpc),
            map(_ => Actions.fetchFavouriteActivityTypes({ siteId })),
            catchError(e => handleError(e, siteId)),
          )),
    )

const deleteFavouriteActivityTypeRpc = (request: DeleteFavouriteActivityTypeRequest): Observable<google_protobuf_empty_pb.Empty> =>
  grpc(() => activityTypeServiceClient.deleteFavouriteActivityType(request, null))

const handleError = (e: unknown, siteId: SiteId) => {
  if (isGrpcError(e)) {
    const { code } = e
    if (code === StatusCode.PERMISSION_DENIED) {
      return concat(
        of(showSnackBar({
          message: t({
            id: "msg_unfavourite_activity_type_permission_denied",
            message: "Failed to unfavourite activity type. You do not have permission to access this activity type."
          }),
          messageType: "ERROR",
        })),
        of(Actions.fetchFavouriteActivityTypes({ siteId }))
      )
    }
  }

  return of(showSnackBar({
    message: t({
      id: "msg_unfavourite_activity_type_failed",
      message: "Failed to unfavourite activity type. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com."
    }),
    messageType: "ERROR",
  }))
}

export default deleteFavouriteActivityType(deleteFavouriteActivityTypeRpc)
